import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/landing/Navbar";
import { Hero } from "@/components/landing/Hero";
import { EditorialMarquee, CountriesStrip } from "@/components/landing/Marquee";
import { TrustSignals } from "@/components/landing/TrustSignals";
import { Services } from "@/components/landing/Services";
import { Journey } from "@/components/landing/Journey";
import { Manifesto } from "@/components/landing/Manifesto";
import { Subjects } from "@/components/landing/Subjects";
import { Calculator } from "@/components/landing/Calculator";
import { Samples } from "@/components/landing/Samples";
import { BlogPreview } from "@/components/landing/BlogPreview";
import { FindSupport } from "@/components/landing/FindSupport";
import { Reviews } from "@/components/landing/Reviews";
import { Faq } from "@/components/landing/Faq";
import { UploadBrief } from "@/components/landing/UploadBrief";
import { Contact } from "@/components/landing/Contact";
import { OrderCTA } from "@/components/landing/OrderCTA";
import { Footer } from "@/components/landing/Footer";
import { WhatsAppFloat } from "@/components/landing/WhatsAppFloat";
import { StickyCTA } from "@/components/landing/StickyCTA";
import { scrollToSection } from "@/lib/scrollTo";
import { BlogList } from "@/pages/BlogList";
import { BlogPost } from "@/pages/BlogPost";
import { PolicyPage } from "@/pages/Policies";
import { WhyChooseUs } from "@/pages/WhyChooseUs";
import { AdminLogin } from "@/pages/admin/AdminLogin";
import { AdminDashboard } from "@/pages/admin/AdminDashboard";
import { OrderPage } from "@/pages/OrderPage";
import { OrderResult } from "@/pages/OrderResult";

const Landing = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => scrollToSection(hash), 700);
      return () => clearTimeout(t);
    }
  }, [hash]);

  return (
    <>
      <Navbar />
      <main>
        <Hero />
        <EditorialMarquee />
        <TrustSignals />
        <Services />
        <Journey />
        <Manifesto />
        <CountriesStrip />
        <Subjects />
        <Calculator />
        <Samples />
        <BlogPreview />
        <FindSupport />
        <Reviews />
        <Faq />
        <UploadBrief />
        <Contact />
        <OrderCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
      <StickyCTA />
    </>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App" data-testid="app-root">
      <div className="noise-overlay" />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/why-choose-us" element={<WhyChooseUs />} />
          <Route path="/refund-policy" element={<PolicyPage policyKey="refund" />} />
          <Route path="/terms-and-conditions" element={<PolicyPage policyKey="terms" />} />
          <Route path="/privacy-policy" element={<PolicyPage policyKey="privacy" />} />
          <Route path="/revision-policy" element={<PolicyPage policyKey="revision" />} />
          <Route path="/cancellation-policy" element={<PolicyPage policyKey="cancellation" />} />
          <Route path="/order" element={<OrderPage />} />
          <Route path="/order/result" element={<OrderResult />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="light" position="bottom-center" toastOptions={{ style: { background: "#FFFFFF", border: "1px solid rgba(160,126,31,0.4)", color: "#12294D" } }} />
    </div>
  );
}

export default App;
